// ================================================================
// 管理員問題管理頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-26
// 功能：管理諮詢問題的顯示狀態（啟用/停用）
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  Toolbar,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Cancel,
  SelectAll,
  Deselect,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getAdminQuestions,
  toggleQuestionStatus,
  batchUpdateQuestions,
  Question,
  CategoryStats,
} from '../../api/admin';

// 類別名稱映射
const categoryNames: { [key: string]: string } = {
  FULL: '完整分析',
  PAIR: '配對分析',
  MAIN: '自我分析',
  COMMON: '常見問題',
  SIMPLE: '優惠咨詢',
};

// 類別顏色
const categoryColors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' } = {
  FULL: 'primary',
  PAIR: 'secondary',
  MAIN: 'success',
  COMMON: 'warning',
  SIMPLE: 'info',
};

const AdminQuestionsPage: React.FC = () => {
  // 狀態
  const [questions, setQuestions] = useState<Question[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  // 載入問題列表
  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAdminQuestions(categoryFilter);
      if (response.success) {
        setQuestions(response.questions || []);
        setCategoryStats(response.category_stats || []);
      } else {
        setError(response.message || '無法載入問題列表');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // 切換單一問題狀態
  const handleToggle = async (questionId: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const response = await toggleQuestionStatus(questionId);
      if (response.success) {
        setSuccess(response.message);
        // 更新本地狀態
        setQuestions(prev =>
          prev.map(q =>
            q.question_id === questionId
              ? { ...q, is_active: response.is_active }
              : q
          )
        );
        // 重新載入統計
        loadQuestions();
      } else {
        setError(response.message || '操作失敗');
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 批次啟用
  const handleBatchEnable = async () => {
    if (selectedIds.size === 0) return;
    setIsProcessing(true);
    setError(null);
    try {
      const response = await batchUpdateQuestions(Array.from(selectedIds), 1);
      if (response.success) {
        setSuccess(response.message);
        setSelectedIds(new Set());
        loadQuestions();
      } else {
        setError(response.message || '操作失敗');
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 批次停用
  const handleBatchDisable = async () => {
    if (selectedIds.size === 0) return;
    setIsProcessing(true);
    setError(null);
    try {
      const response = await batchUpdateQuestions(Array.from(selectedIds), 0);
      if (response.success) {
        setSuccess(response.message);
        setSelectedIds(new Set());
        loadQuestions();
      } else {
        setError(response.message || '操作失敗');
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 全選/取消全選
  const handleSelectAll = () => {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.question_id)));
    }
  };

  // 選擇單一項目
  const handleSelectOne = (questionId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedIds(newSelected);
  };

  // 計算統計
  const totalQuestions = categoryStats.reduce((sum, c) => sum + c.total, 0);
  const totalActive = categoryStats.reduce((sum, c) => sum + c.active, 0);

  if (isLoading && questions.length === 0) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              問題顯示管理
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理諮詢問題的顯示狀態，停用的問題將不會顯示給使用者
            </Typography>
          </Box>
          <Tooltip title="重新載入">
            <IconButton onClick={loadQuestions} disabled={isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 訊息提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* 統計卡片 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  問題總數
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {totalQuestions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  啟用中
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {totalActive}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  已停用
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {totalQuestions - totalActive}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  已選取
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {selectedIds.size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 類別統計 */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {categoryStats.map(stat => (
            <Chip
              key={stat.category_id}
              label={`${categoryNames[stat.category_id] || stat.category_id}: ${stat.active}/${stat.total}`}
              color={categoryColors[stat.category_id] || 'default'}
              variant={categoryFilter === stat.category_id ? 'filled' : 'outlined'}
              onClick={() => setCategoryFilter(stat.category_id === categoryFilter ? 'all' : stat.category_id)}
            />
          ))}
        </Box>

        {/* 工具列 */}
        <Paper sx={{ mb: 2 }}>
          <Toolbar sx={{ gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>類別篩選</InputLabel>
              <Select
                value={categoryFilter}
                label="類別篩選"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">全部類別</MenuItem>
                <MenuItem value="FULL">完整分析</MenuItem>
                <MenuItem value="PAIR">配對分析</MenuItem>
                <MenuItem value="MAIN">自我分析</MenuItem>
                <MenuItem value="COMMON">常見問題</MenuItem>
                <MenuItem value="SIMPLE">優惠咨詢</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={selectedIds.size === questions.length ? <Deselect /> : <SelectAll />}
              onClick={handleSelectAll}
            >
              {selectedIds.size === questions.length ? '取消全選' : '全選'}
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<Visibility />}
              onClick={handleBatchEnable}
              disabled={selectedIds.size === 0 || isProcessing}
            >
              批次啟用
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<VisibilityOff />}
              onClick={handleBatchDisable}
              disabled={selectedIds.size === 0 || isProcessing}
            >
              批次停用
            </Button>
          </Toolbar>
        </Paper>

        {/* 問題列表 */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.size === questions.length && questions.length > 0}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < questions.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>問題 ID</TableCell>
                <TableCell>類別</TableCell>
                <TableCell>問題內容</TableCell>
                <TableCell align="right">價格</TableCell>
                <TableCell align="center">狀態</TableCell>
                <TableCell align="center">顯示</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((question) => (
                <TableRow
                  key={question.question_id}
                  sx={{
                    opacity: question.is_active ? 1 : 0.6,
                    bgcolor: selectedIds.has(question.question_id) ? 'action.selected' : 'inherit',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(question.question_id)}
                      onChange={() => handleSelectOne(question.question_id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {question.question_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categoryNames[question.category_id] || question.category_id}
                      size="small"
                      color={categoryColors[question.category_id] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 400 }} noWrap>
                      {question.question_text}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {question.is_free ? (
                      <Chip label="免費" size="small" color="success" />
                    ) : (
                      <Typography variant="body2">NT$ {question.price}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {question.is_active ? (
                      <Chip
                        icon={<CheckCircle />}
                        label="啟用"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        icon={<Cancel />}
                        label="停用"
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={question.is_active === 1}
                      onChange={() => handleToggle(question.question_id)}
                      disabled={isProcessing}
                      color="success"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {questions.length === 0 && !isLoading && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">沒有符合條件的問題</Typography>
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
};

export default AdminQuestionsPage;
